import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: ReactNode;
  trend?: 'up' | 'down' | 'stable';
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

const variantBorder: Record<string, string> = {
  default: 'border-gray-200 dark:border-gray-800',
  success: 'border-emerald-800/50',
  warning: 'border-amber-800/50',
  danger: 'border-red-800/50',
};

const variantIconBg: Record<string, string> = {
  default: 'bg-gray-100 dark:bg-gray-800',
  success: 'bg-emerald-900/50',
  warning: 'bg-amber-900/50',
  danger: 'bg-red-900/50',
};

export function MetricCard({ title, value, subtitle, icon, trend, variant = 'default' }: MetricCardProps) {
  return (
    <div className={clsx('card-compact flex items-start gap-4', variantBorder[variant])}>
      {icon && (
        <div className={clsx('p-2.5 rounded-lg shrink-0', variantIconBg[variant])}>
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
        <div className="flex items-baseline gap-2 mt-1">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          {trend && (
            <span className="flex items-center">
              {trend === 'up' && <TrendingUp className="h-4 w-4 text-emerald-400" />}
              {trend === 'down' && <TrendingDown className="h-4 w-4 text-red-400" />}
              {trend === 'stable' && <Minus className="h-4 w-4 text-gray-500" />}
            </span>
          )}
        </div>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}
